import { useState, useEffect, useCallback } from "react";
import { Paper } from "@mui/material";
import { Spinner, Input } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  getInvoiceCategories,
  createInvoiceCategory,
  updateInvoiceCategory,
  deleteInvoiceCategory,
} from "./Api";

const FInput = ({ value, onChange, placeholder }) => (
  <Input
    type="text"
    bsSize="sm"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{ borderColor: "#E2E8F0", borderRadius: 7, fontSize: "0.82rem" }}
  />
);

const InlineCategoryRow = ({ data, isNew, onSave, onCancel }) => {
  const [row, setRow] = useState({ isActive: true, ...data });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setRow((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!row.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try { await onSave(row); } finally { setSaving(false); }
  };

  return (
    <tr style={{ background: isNew ? "#ECFDF5" : "#EEF2FF" }}>
      <td style={{ width: 40, textAlign: "center", padding: "8px 10px" }}>
        <FeatherIcon icon={isNew ? "plus" : "edit-2"} size={12} color={isNew ? "#059669" : "#4F46E5"} />
      </td>
      <td style={{ minWidth: 200, padding: "8px 10px" }}>
        <FInput value={row.name} onChange={set("name")} placeholder="Category name…" />
      </td>
      <td style={{ width: 100, textAlign: "center", padding: "8px 10px" }}>
        {isNew ? (
          <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>Active</span>
        ) : (
          <select
            value={row.isActive ? "true" : "false"}
            onChange={(e) => set("isActive")(e.target.value === "true")}
            style={{
              border: "1px solid #E2E8F0", borderRadius: 7, fontSize: "0.78rem",
              padding: "4px 8px", cursor: "pointer", outline: "none",
              background: row.isActive ? "#ECFDF5" : "#FEF2F2",
              color: row.isActive ? "#059669" : "#DC2626",
              fontWeight: 700, width: "100%",
            }}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        )}
      </td>
      <td style={{ width: 130, textAlign: "center", padding: "8px 10px" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={onCancel} disabled={saving}>
            <FeatherIcon icon="x" size={12} /> Cancel
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={12} />}
            {isNew ? "Add" : "Save"}
          </button>
        </div>
      </td>
    </tr>
  );
};

const InvoiceCategoriesTable = ({ userRole, canManage, onRefresh }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // Company accounts and lead technical staff may manage categories.
  const isCompany = canManage ?? ((userRole || "").toLowerCase() === "company");

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoiceCategories(showInactive ? {} : { activeOnly: "true" });
      setCategories(res?.data || []);
    } catch {
      // toast handled
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleCreate = async (row) => {
    await createInvoiceCategory({ name: row.name.trim(), description: row.description?.trim() || "" });
    setShowNewRow(false);
    fetchCategories();
    onRefresh?.();
  };

  const handleUpdate = async (id, row) => {
    await updateInvoiceCategory(id, {
      name: row.name.trim(),
      description: row.description?.trim() || "",
      isActive: row.isActive,
    });
    setEditingId(null);
    fetchCategories();
    onRefresh?.();
  };

  const handleDelete = async (cat) => {
    const res = await Swal.fire({
      title: "Deactivate Category?",
      html: `<p>Deactivate <strong>${cat.name}</strong>? It will no longer appear when creating invoices.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Deactivate",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteInvoiceCategory(cat._id);
      fetchCategories();
      onRefresh?.();
    } catch {
      // handled
    }
  };

  return (
    <Paper elevation={0} className="iv-wrap iv-paper">
      <div className="iv-header">
        <div className="iv-header__left">
          <div className="iv-header__icon-wrap">
            <FeatherIcon icon="layers" size={18} color="#A5B4FC" />
          </div>
          <div className="iv-header__meta">
            <div className="iv-header__label">Finance</div>
            <div className="iv-header__title">Invoice Categories</div>
          </div>
        </div>
        <div className="iv-header__actions" style={{ gap: 10 }}>
          <label style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem",
            color: "#475569", fontWeight: 600, cursor: "pointer",
          }}>
            <Input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              style={{ marginTop: 0 }}
            />
            Show inactive
          </label>
          {isCompany && (
            <button
              type="button"
              className="iv-btn iv-btn--primary"
              onClick={() => setShowNewRow(true)}
              disabled={showNewRow}
            >
              <FeatherIcon icon="plus" size={14} />
              New Category
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="iv-tbl" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th style={{ minWidth: 240, textAlign: "left", paddingLeft: 10 }}>Categories</th>
              <th style={{ width: 100 }}>Status</th>
              {isCompany && <th style={{ width: 130 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <InlineCategoryRow
                isNew
                data={{ name: "", description: "" }}
                onSave={handleCreate}
                onCancel={() => setShowNewRow(false)}
              />
            )}

            {loading && (
              <tr>
                <td colSpan={isCompany ? 4 : 3} style={{ textAlign: "center", padding: "40px 0" }}>
                  <Spinner style={{ color: "#4F46E5" }} />
                </td>
              </tr>
            )}

            {!loading && categories.length === 0 && !showNewRow && (
              <tr>
                <td colSpan={isCompany ? 4 : 3} className="iv-empty-cell">
                  <div className="iv-empty" style={{ minHeight: "20vh" }}>
                    <div className="iv-empty__icon-wrap">
                      <FeatherIcon icon="layers" size={22} color="#4F46E5" />
                    </div>
                    <div className="iv-empty__title">No Categories</div>
                    <div className="iv-empty__sub">
                      {isCompany ? "Add your first category to start grouping invoices." : "Ask a company admin to add categories."}
                    </div>
                  </div>
                </td>
              </tr>
            )}

            {!loading && categories.map((cat) => {
              if (editingId === cat._id) {
                return (
                  <InlineCategoryRow
                    key={cat._id}
                    data={cat}
                    onSave={(row) => handleUpdate(cat._id, row)}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }
              return (
                <tr key={cat._id} className="iv-row">
                  <td style={{ textAlign: "center", width: 40 }}>
                    <FeatherIcon icon="tag" size={13} color="#94A3B8" />
                  </td>
                  <td style={{ textAlign: "left", paddingLeft: 10, fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>
                    {cat.name}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span style={{
                      background: cat.isActive ? "#ECFDF5" : "#FEF2F2",
                      color: cat.isActive ? "#059669" : "#DC2626",
                      border: cat.isActive ? "1px solid #A7F3D0" : "1px solid #FECDD3",
                      borderRadius: 20, padding: "2px 10px",
                      fontSize: "0.72rem", fontWeight: 700,
                    }}>
                      {cat.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isCompany && (
                    <td style={{ textAlign: "center" }}>
                      <div className="iv-action-group">
                        <button type="button" className="iv-ebtn" title="Edit" onClick={() => setEditingId(cat._id)}>
                          <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                        </button>
                        {cat.isActive && (
                          <button type="button" className="iv-dbtn" title="Deactivate" onClick={() => handleDelete(cat)}>
                            <FeatherIcon icon="x-circle" size={13} color="#DC2626" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

export { InvoiceCategoriesTable };
